import { FamilyDto } from 'api/families/models/FamilyDto';
import PageBreadcrumbsPortal from 'common/components/pageBreadcrumbsPortal/PageBreadcrumbsPortal';
import PageContainer from 'common/components/pageContainer/PageContainer';
import PageHeader from 'common/components/pageHeader/PageHeader';
import ScreenTitle from 'common/components/screenTitle/ScreenTitle';
import Logger from 'common/services/Logger';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LOGGER_LOG_TYPE } from 'Config';
import FamiliesService from 'api/families/FamiliesService';
import { useNavigate, useParams } from 'react-router-dom';
import Loading from 'common/services/Loading';
import { useForm } from 'react-hook-form';
import { Col, OverlayTrigger, Row, Tooltip } from 'react-bootstrap';
import { Label } from 'common/components/texts/Texts';
import TextInput, { TextInputController } from 'common/components/textInput/TextInput';
import { RichInputController } from 'common/components/richTextInput/RichTextInput';
import Button from 'common/components/button/Button';
import ConfirmDeleteModal from 'common/components/modal/confirmDeleteModal/ConfirmDeleteModal';
import { FaTrashAlt } from 'react-icons/fa';
import { CheckInputController } from 'common/components/checkInput/CheckInput';
import InputError from 'common/components/inputError/InputError';
import { SearchableSelectInputController } from 'common/components/searchableSelectInput/SearchableSelectInputController';
import { Reducers } from 'store/types';
import { useSelector } from 'react-redux';
import UsersService from 'api/users/UsersService';
import { UserProfile } from 'api/account/models/UserProfile';
import Toast from 'common/services/Toast';
import InfoIcon from 'common/components/infoIcon/InfoIcon';
import InformationModal from 'common/components/modal/informationModal/InformationModal';
import { ParameterSectionBaseModel, ParameterSectionParameterType, ParameterSectionType, ParameterVisibility } from 'common/components/parametersSections/models';
import { SearchableSelectTitleSubTitleOption } from 'common/components/searchableSelectInput/optionsFormats/OptionTitleSubTitle';
import { SelectInputController, SelectInputOption } from 'common/components/selectInput/SelectInput';
import WorkTypesService from 'api/workTypes/WorkTypesService';
import UnitsService from 'api/units/UnitsService';
import type { CatalogParameterDto } from 'api/catalogParameters/models/CatalogParameterDto';
import { Form } from 'common/components/form/Form';
import { IoAddSharp } from 'react-icons/io5';
import styles from './FamilyScreen.module.scss';
import { cloneParametersForImport, newSection, normalizeSubsectionVisibility, onMove } from './parameters/utils';
import { SortableTree } from 'common/components/sortableTree/SortableTree';
import { FlattenedItem, ItemChangedReason } from 'common/components/sortableTree/types';
import ParameterLine from './parameters/ParameterLine';
import { AtLeast } from 'common/types/Atleast';
import { FamilyParameterDataProvider } from './parameters/FamilyParameterDataProvider';
import YesNoModal from 'common/components/modal/yesNoModal/YesNoModal';
import FilesUploader from 'common/components/filesUploader/FilesUploader';
import { AttachmentDto } from 'api/common/models/AttachmentDto';
import CustomFile from 'common/models/CustomFile';
import Utils from 'common/services/Utils';

import ExpressionSystem, { ExpressionParameter } from 'common/components/expressionButton/ExpressionSystem';

type FamiliesListType = 'elements' | 'components';

interface Props {
    listType: FamiliesListType;
    isSubFamilies?: boolean;
}

function FamilyScreen({ listType, isSubFamilies }: Props): JSX.Element | null {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { id, type } = useParams<{ id: string, type: 'create' | 'details' | 'edit' | 'duplicate' }>();
    const form = useForm<FamilyDto>({ shouldUnregister: false });
    const errors = form.formState.errors;
    const url = '/library/' + listType + (isSubFamilies ? '/sub-families' : '/families');
    const currentUrl = url + '/' + type + (id ? ('/' + id) : '');
    const isDetails = type === 'details';
    const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);
    const [showConfirmDeleteRelationsModal, setShowConfirmDeleteRelationsModal] = useState(false);
    const loggedUser = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const canWrite = UsersService.hasPolicies(loggedUser?.policies ?? [], ['FAMILIES_WRITE'])
    const canRead = UsersService.hasPolicies(loggedUser?.policies ?? [], ['FAMILIES_READ'])
    const [showTypesInformationModal, setShowTypesInformationModal] = useState(false);
    const isStart = useRef<boolean>(true);
    const [familiesInputOptions, setFamiliesInputOptions] = useState<SearchableSelectTitleSubTitleOption[]>([])
    const [sections, setSections] = useState<ParameterSectionBaseModel[]>([])
    const [workTypes, setWorkTypes] = useState<SelectInputOption[]>([]);
    const [units, setUnits] = useState<SearchableSelectTitleSubTitleOption[]>([]);
    const [selectedSectionKey, setSelectedSectionKey] = useState<string>('');
    const [family, setFamily] = useState<Partial<FamilyDto>>();
    const [medias, setMedias] = useState<AttachmentDto[]>([]);
    const [priceExpression, setPriceExpression] = useState<string | null>(null);
    const [averageExpression, setAverageExpression] = useState<string | null>(null);
    const [weightUnitId, setWeightUnitId] = useState<string | null>(null);
    const [catalogParameters, setCatalogParameters] = useState<CatalogParameterDto[]>([]);
    const [pendingParentFamilyId, setPendingParentFamilyId] = useState<string | null>(null);
    const [showImportParametersModal, setShowImportParametersModal] = useState(false);
    const [pendingParentWorkTypeId, setPendingParentWorkTypeId] = useState<string | null>(null);
    const [showImportWorkTypeModal, setShowImportWorkTypeModal] = useState(false);
    const [showCascadeWorkTypeModal, setShowCascadeWorkTypeModal] = useState(false);
    const pendingSaveRef = useRef<{ model: FamilyDto, medias: CustomFile[] } | null>(null);
    const initialWorkTypeIdRef = useRef<string | null>(null);

    const getData = async () => {
        try {
            let result: Partial<FamilyDto> = {
                id,
                isComponent: listType === 'components',
                isElement: listType === 'elements',
                name: '',
                description: '',
                parentId: null,
            }

            Loading.show();

            if (id) {
                result = await FamiliesService.getById(id);
                if (result?.parameters?.length) {
                    result.parameters.forEach((x) => {
                        x.isOpen = true;
                    })
                    setSections(result.parameters);
                }

                if (result?.images?.length) {
                    setMedias(result.images.map(img => ({
                        id: img.id ?? null,
                        key: img.attachmentId ?? '',
                        attachmentId: img.attachmentId,
                        url: img.url ?? '',
                        name: img.name ?? '',
                        contentType: img.mimeType ?? '',
                        size: img.size ?? 0,
                        tempFile: null,
                        isMain: img.isMain ?? false
                    })));
                } else {
                    setMedias([]);
                }

                if (result && type === 'duplicate') {
                    result.name += ' - ' + t('common.copy');

                    // Replace original family's FAMILY reference with SELF placeholder
                    // so the backend can replace it with the new family's ID on create
                    const originalGuidClean = id.replace(/-/g, '').toLowerCase();
                    const familyPattern = `M_FAMILY__${originalGuidClean}_E`;
                    const selfPattern = 'M_FAMILY__SELF_E';

                    if (result.priceExpression) {
                        result.priceExpression = result.priceExpression.replaceAll(familyPattern, selfPattern);
                    }
                    if (result.averageExpression) {
                        result.averageExpression = result.averageExpression.replaceAll(familyPattern, selfPattern);
                    }
                    result.parameters?.forEach(p => {
                        if (p.formulaExpression) {
                            p.formulaExpression = p.formulaExpression.replaceAll(familyPattern, selfPattern);
                        }
                    });
                }

                setPriceExpression(result?.priceExpression ?? null);
                setAverageExpression(result?.averageExpression ?? null);
            } else {
                setMedias([]);
                setPriceExpression(null);
                setAverageExpression(null);
            }

            const famResult = await FamiliesService.catalog();
            setFamiliesInputOptions(famResult);

            initialWorkTypeIdRef.current = result.workTypeId ?? null;

            const ensureWorkTypeId = result.workTypeId ?? result.parentWorkTypeId ?? undefined;
            const workTypesResult = await WorkTypesService.getCatalog({ workTypeId: ensureWorkTypeId });
            setWorkTypes(workTypesResult);

            const CatalogParametersService = (await import('api/catalogParameters/CatalogParametersService')).default;
            const catalogParamsResult = await CatalogParametersService.getCatalog();
            setCatalogParameters(catalogParamsResult);

            const unitsResult = await UnitsService.getFormattedCatalog();
            const kgUnit = unitsResult.find(x => x.symbol.toLowerCase() === 'kg');
            setWeightUnitId(kgUnit?.id ?? null);
            setUnits(unitsResult.map(x => {
                return {
                    value: x.id,
                    label: x.name,
                    subTitle: ''
                }
            }))

            form.reset(result);
            setFamily(result);
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t get families', error);
            Toast.error(t('messages.error_load_info'));
        } finally {
            Loading.hide();
        }
    }

    const onSubmit = async (model: FamilyDto) => {
        try {
            Loading.show();

            // if (sections && model.isComponent && sections.find(x => x.type === ParameterSectionType.PARAMETER && x.parameterType !== ParameterSectionParameterType.PRODUCTS)) {
            //     Toast.warning(t('families.family.family_component_error_save'));
            //     Loading.hide();
            //     return;
            // }

            const trimmedName = model.name?.trim() ?? '';
            if (trimmedName) {
                const dupResult = await FamiliesService.getList({
                    name: trimmedName,
                    isElement: listType === 'elements',
                    isComponent: listType === 'components',
                    hasParentId: Boolean(isSubFamilies),
                    page: 1,
                    itemsPerPage: 50,
                    orderColumn: 'name',
                    orderBy: 'asc'
                });
                const duplicate = dupResult.items?.find(f =>
                    f.name?.trim().toLowerCase() === trimmedName.toLowerCase() &&
                    (type === 'duplicate' || f.id !== model.id)
                );
                if (duplicate) {
                    const msgKey = isSubFamilies
                        ? 'families.family.sub_family_name_already_exists'
                        : 'families.family.family_name_already_exists';
                    Toast.warning(t(msgKey));
                    form.setError('name', { type: 'manual', message: t(msgKey) });
                    Loading.hide();
                    return;
                }
            }

            // Validate only one WEIGHT parameter per family
            const weightParams = sections.filter(x => x.parameterType === ParameterSectionParameterType.WEIGHT);
            if (weightParams.length > 1) {
                Toast.warning(t('families.family.family_weight_error'));
                Loading.hide();
                return;
            }

            model.parameters = normalizeSubsectionVisibility(sections);
            model.priceExpression = priceExpression;
            model.averageExpression = averageExpression;

            // Process images
            model.imagesToRemove = [];
            model.imagesIds = [];
            const mediasToSend: CustomFile[] = [];

            medias.forEach((element) => {
                if (element.remove && element.id) {
                    model.imagesToRemove?.push(element.id);
                } else if (!element.remove) {
                    if (element.isMain) {
                        model.mainImageKey = element.key ?? element.attachmentId;
                    }

                    if (element.tempFile) {
                        element.tempFile.containerName = element.key;
                        mediasToSend.push(element.tempFile);
                        model.imagesIds?.push(element.key);
                    }
                }
            });

            if (model.mainImageKey == null && medias.filter(m => !m.remove).length > 0) {
                const firstMedia = medias.find(m => !m.remove);
                model.mainImageKey = firstMedia?.key ?? firstMedia?.attachmentId;
            }

            const isMainFamilyEdit = !isSubFamilies && Boolean(model.id) && type !== 'duplicate';
            const workTypeChanged = (initialWorkTypeIdRef.current ?? null) !== (model.workTypeId ?? null);
            if (isMainFamilyEdit && family?.hasSubFamilies && workTypeChanged) {
                pendingSaveRef.current = { model, medias: mediasToSend };
                setShowCascadeWorkTypeModal(true);
                Loading.hide();
                return;
            }

            Loading.hide();
            await doSave(model, mediasToSend, false);
        } catch (error: any) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t update save the family', error);
            Toast.error(t('messages.record_save_error'));
            Loading.hide();
        }
    }

    const doSave = async (model: FamilyDto, mediasToSend: CustomFile[], applyWorkTypeToSubFamilies: boolean) => {
        try {
            Loading.show();
            model.applyWorkTypeToSubFamilies = applyWorkTypeToSubFamilies;
            if (model.id && type !== 'duplicate') {
                await FamiliesService.update(model, mediasToSend);
                void getData();
            } else {
                const newId = await FamiliesService.create(model, mediasToSend);
                navigateTo('edit', newId);
            }
            Toast.success(t('messages.record_save_success'));
        } catch (error: any) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t update save the family', error);
            Toast.error(t('messages.record_save_error'));
        } finally {
            Loading.hide();
        }
    }

    const onConfirmCascadeWorkType = async (apply: boolean) => {
        setShowCascadeWorkTypeModal(false);
        const pending = pendingSaveRef.current;
        pendingSaveRef.current = null;
        if (!pending) {
            return;
        }
        await doSave(pending.model, pending.medias, apply);
    }

    const onInvalid = () => {
        Toast.warning(t('messages.required_fields_empty'));
    }

    const onDelete = async (result: boolean) => {
        setShowConfirmDeleteModal(false);

        if (result) {
            setShowConfirmDeleteRelationsModal(true);
        }
    }

    const onDeleteRelations = async (result: boolean) => {
        Loading.show();
        try {
            await FamiliesService.delete(id!, result)
            Toast.success(t('messages.record_delete_success'));
            onGoBack();
        } catch (error: any) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t delete family', error);
            Toast.error(t('messages.record_delete_error'));
        } finally {
            Loading.hide();
            setShowConfirmDeleteRelationsModal(false)
        }
    }

    const onGoBack = () => {
        navigate(url);
    }

    const navigateTo = (type: string, id?: string) => {
        navigate(url + '/' + type + (id ? ('/' + id) : ''));
    }

    const onLoadFamiliesParentsOptions = (
        inputValue: string,
        callback: (options: any[], hasMore: boolean) => void,
        _isStart: boolean,
        page: number
    ) => {
        FamiliesService.getList({
            hasParentId: false,
            isComponent: listType === 'components',
            isElement: listType === 'elements',
            itemsPerPage: 10,
            page,
            name: isStart.current ? form.getValues('parentName') ?? inputValue : inputValue,
        }).then(result => {
            isStart.current = false;

            const options: FamilyDto[] = result.items.map(item => ({ ...item, label: item.name, value: item.id, }))
            callback(options, result.currentPage < result.totalPages);
        }).catch((error) => {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t get families', error);
            Toast.error(t('messages.error_load_info'));
        });
    };

    const onClickAddNewSection = () => {
        const section = newSection(null);
        if (family?.isComponent) {
            section.visibility = ParameterVisibility.BUDGET_ONLY;
        }
        setSections([...sections, ...[section]]);
    }

    const onItemChange = (item: AtLeast<FlattenedItem<ParameterSectionBaseModel>, 'key'>, reason: 'change' | 'remove' | 'change-self-and-children') => {
        const removeDescendants = (lines: ParameterSectionBaseModel[], parentKey: string): ParameterSectionBaseModel[] => {
            const directChildren = lines.filter(line => line.parentKey === parentKey);

            directChildren.forEach(child => {
                lines = removeDescendants(lines, child.key);
            });

            return lines.filter(line => line.parentKey !== parentKey);
        };

        const alterDescendantsVisibility = (lines: ParameterSectionBaseModel[], key: string, value: ParameterVisibility): ParameterSectionBaseModel[] => {
            lines.forEach(x => {
                if (x.key === key) {
                    x.visibility = value
                }
            })

            const children = lines.filter(x => x.parentKey === key);

            children.forEach(child => {
                child.visibility = value;
                lines = alterDescendantsVisibility(lines, child.key, value);
            });

            return lines
        };

        if (reason === 'remove') {
            let updatedLines = sections.filter(line => line.key !== item.key);
            updatedLines = removeDescendants(updatedLines, item.key);
            setSections([...updatedLines])
        }

        if (reason === 'change') {
            setSections(sections.map((section) => {
                if (section.key === item.key) {
                    return { ...section, ...item };
                }
                return section;
            }));
        }

        if (reason === 'change-self-and-children') {
            const newLines = alterDescendantsVisibility(sections, item.key, item.visibility ?? ParameterVisibility.BOTH)
            setSections([...newLines])
        }
    }

    const onItemCreate = (item: ParameterSectionBaseModel) => {
        if (item.parentKey === null) {
            let newSections = sections;
            const index = sections.findIndex(l => l.key === item.relativeKey);
            if (index < 0) {
                newSections = [...newSections, item]
            } else {
                const totalChildren = getTotalLinesInside(sections, item.relativeKey!);
                newSections = [
                    ...newSections.slice(0, index + 1 + totalChildren),
                    item,
                    ...newSections.slice(index + 1 + totalChildren),
                ];
            }
            setSections(newSections)
        } else {
            let newLines = [...sections];
            const index = sections.findIndex(l => l.key === (item.relativeKey ?? item.parentKey));
            if (index > -1) {
                const relativeItem = sections[index];
                const totalChildren = relativeItem.key !== item.parentKey && relativeItem.type === ParameterSectionType.SECTION && item.type === ParameterSectionType.SECTION
                    ? getTotalLinesInside(sections, item.relativeKey!)
                    : 0;
                newLines = [
                    ...newLines.slice(0, index + 1 + totalChildren),
                    item,
                    ...newLines.slice(index + 1 + totalChildren),
                ];
            }
            newLines.map(x => {
                if (x.key === item.parentKey) {
                    x.isOpen = true;
                }
                return x;
            })
            setSections([...newLines])
        }
    }

    const onMoveItem = (key: string, type: 'up' | 'down') => {
        const newLines = onMove(sections, key, type)

        setSections([...newLines])
    }

    function getTotalLinesInside(lines: ParameterSectionBaseModel[], lineKey: string | null) {
        const children = lines.filter(x => x.parentKey === lineKey);

        let totalCount = children.length;

        for (const child of children) {
            totalCount += getTotalLinesInside(lines, child.key);
        }

        return totalCount;
    }

    const onClickBody = (ev: MouseEvent) => {
        if (ev && ev.target) {
            const target = ev.target as HTMLElement;
            const targetId = target.id;

            if (targetId.includes('react-select-')) {
                return;
            }
        }

        if (selectedSectionKey) {
            setSelectedSectionKey('');
        }
    }

    const onChangeFamilyType = () => {
        setFamily({ ...family, isComponent: form.getValues('isComponent'), isElement: form.getValues('isElement') });
    }

    const onConfirmImportWorkType = async (confirm: boolean) => {
        setShowImportWorkTypeModal(false);
        const workTypeIdToImport = pendingParentWorkTypeId;
        setPendingParentWorkTypeId(null);
        if (confirm && workTypeIdToImport) {
            form.setValue('workTypeId', workTypeIdToImport);
            try {
                const updated = await WorkTypesService.getCatalog({ workTypeId: workTypeIdToImport });
                setWorkTypes(updated);
            } catch (error) {
                Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t reload work types catalog', error);
            }
        }
        if (pendingParentFamilyId) {
            setShowImportParametersModal(true);
        }
    }

    const onConfirmImportParameters = async (confirm: boolean) => {
        setShowImportParametersModal(false);
        const parentId = pendingParentFamilyId;
        setPendingParentFamilyId(null);
        if (!confirm || !parentId) {
            return;
        }
        try {
            Loading.show();
            const parameters = await FamiliesService.getFamilyParameters(parentId);
            const cloned = cloneParametersForImport(parameters ?? []);
            setSections(cloned);
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t import parameters from parent family', error);
            Toast.error(t('messages.error_load_info'));
        } finally {
            Loading.hide();
        }
    }

    useEffect(() => {
        document.body.addEventListener('click', onClickBody);

        return function cleanup() {
            window.removeEventListener('click', onClickBody);
        }
    }, [selectedSectionKey, setSelectedSectionKey]);

    useEffect(() => {
        void getData();
    }, [id, type]);

    const loadFamilyPriceFormulaParameters = (): ExpressionParameter[] => {
        const params: ExpressionParameter[] = (sections || [])
            .filter(p =>
                p.type === ParameterSectionType.PARAMETER &&
                ['NUMBER', 'QUANTITY', 'PRICE', 'NUMBER_MIN_MAX', 'WEIGHT'].includes(p.parameterType ?? '')
            )
            .map(p => ({
                id: ExpressionSystem.formatParameterId([p.id || p.key]),
                name: ExpressionSystem.formatParameterName([p.parameterName || '']),
                value: 0
            }));

        // Add family average option (uses SELF placeholder when creating/duplicating, real ID when editing)
        const familyAvgId = (id && type !== 'duplicate') ? id : 'SELF';
        params.push({
            id: ExpressionSystem.formatParameterId(['FAMILY', familyAvgId]),
            name: ExpressionSystem.formatParameterName([t('common.parameters_sections.parameters_types.FAMILY_AVERAGE')]),
            value: 0
        });

        // Add element type coefficient option (generic, resolved in element context)
        params.push({
            id: ExpressionSystem.formatParameterId(['ELEMENT_TYPE_COEFFICIENT']),
            name: ExpressionSystem.formatParameterName([t('common.parameters_sections.parameters_types.ELEMENT_TYPE_COEFFICIENT')]),
            value: 1
        });

        return params;
    };

    const loadFamilyAverageFormulaParameters = (): ExpressionParameter[] => {
        const filteredParams = (sections || [])
            .filter(p =>
                p.type === ParameterSectionType.PARAMETER &&
                ['NUMBER', 'QUANTITY', 'PRICE', 'NUMBER_MIN_MAX', 'WEIGHT'].includes(p.parameterType ?? '')
            );

        const listOfLabel = t('common.parameters_sections.list_of');

        // Include both direct and LISTOF (array) options for average formulas
        const params: ExpressionParameter[] = filteredParams.flatMap(p => [
            {
                id: ExpressionSystem.formatParameterId([p.id || p.key]),
                name: ExpressionSystem.formatParameterName([p.parameterName || '']),
                value: 0
            },
            {
                id: ExpressionSystem.formatParameterId(['LISTOF', p.id || p.key]),
                name: ExpressionSystem.formatParameterName([listOfLabel, p.parameterName || '']),
                value: 0
            }
        ]);

        // Also register catalog parameter IDs (and LISTOF variants) so formulas imported from catalog resolve correctly
        for (const p of filteredParams) {
            if (p.catalogParameterId) {
                const catalogId = ExpressionSystem.formatParameterId([p.catalogParameterId]);
                if (!params.some(r => r.id === catalogId)) {
                    params.push({
                        id: catalogId,
                        name: ExpressionSystem.formatParameterName([p.parameterName || '']),
                        value: 0,
                        hidden: true
                    });
                }
                const catalogListOfId = ExpressionSystem.formatParameterId(['LISTOF', p.catalogParameterId]);
                if (!params.some(r => r.id === catalogListOfId)) {
                    params.push({
                        id: catalogListOfId,
                        name: ExpressionSystem.formatParameterName([listOfLabel, p.parameterName || '']),
                        value: 0,
                        hidden: true
                    });
                }
            }
        }

        return params;
    };

    const entityName = form.watch('name');
    const familyKey = listType === 'elements'
        ? (isSubFamilies ? 'elements_sub_family' : 'elements_family')
        : (isSubFamilies ? 'components_sub_family' : 'components_family');
    let listTitle = '';
    switch (listType) {
        case 'elements':
            listTitle = isSubFamilies ? t('families.list.elements_sub_family_title') : t('families.list.elements_family_title');
            break;
        case 'components':
            listTitle = isSubFamilies ? t('families.list.components_sub_family_title') : t('families.list.components_family_title');
            break;
        default:
            break;
    }
    let titlePrefix: string;
    if (type === 'details') {
        titlePrefix = t(`common.details_title.${familyKey}`);
    } else if (type === 'edit') {
        titlePrefix = t(`common.edit_title.${familyKey}`);
    } else {
        titlePrefix = t(`common.new_title.${familyKey}`);
    }
    const entityLabel = type !== 'create' && entityName ? entityName : undefined;
    const title = entityLabel ? `${titlePrefix} ${entityLabel}` : titlePrefix;

    if (!canRead) {
        return null;
    }

    return (
        <ScreenTitle title={title}>
            <PageBreadcrumbsPortal
                breadcrumbs={[
                    { name: t('home.title'), url: '/' },
                    { name: listTitle, url },
                    { name: title, url: currentUrl },
                ]}
            />
            <PageHeader title={titlePrefix} subTitle={entityLabel} informationText={t('common.go_back')} showGoBack onGoBack={onGoBack}>
                {canWrite && !isDetails && <Button form='formfamily' type='submit'>{t('common.save')}</Button>}
                {canWrite && isDetails && <Button fw href={`${url}/duplicate/${id}`}>{t('common.duplicate')}</Button>}
                {canWrite && isDetails && <Button fw href={`${url}/edit/${id}`}>{t('common.edit')}</Button>}
                {canWrite && isDetails && (
                    <Button variant='danger' onClick={() => setShowConfirmDeleteModal(true)}>
                        <FaTrashAlt />
                    </Button>
                )}
            </PageHeader>
            <PageContainer addBottomSpace>
                <Form id='formfamily' handleSubmit={form.handleSubmit} onSubmit={onSubmit} onInvalid={onInvalid} noValidate className={styles.form}>
                    <Row>
                        <Col xs={6} className="mb-3">
                            <Label space>{t('families.family.name')}{!isDetails ? '*' : ''}</Label>
                            <TextInputController
                                name='name'
                                control={form.control}
                                placeholder={t('families.family.name')}
                                disabled={isDetails}
                                rules={{ required: true, maxLength: 500 }}
                                hasError={Boolean(errors.name)}
                            />
                            <InputError error={errors.name} maxLength={500} />
                        </Col>
                        {isSubFamilies && (
                            <Col xs={6} className="mb-3">
                                <Label space>{t('families.family.family')}{!isDetails ? '*' : ''}</Label>
                                <SearchableSelectInputController
                                    name='parentId'
                                    control={form.control}
                                    objectValue={form.getValues('selectObject') as any}
                                    loadOptions={onLoadFamiliesParentsOptions}
                                    disabled={isDetails}
                                    rules={{ required: true }}
                                    hasError={Boolean(errors.parentId)}
                                    onChange={(value, option) => {
                                        if (option) {
                                            form.setValue('isComponent', option.isComponent);
                                            form.setValue('isElement', option.isElement);
                                        }
                                        if (value) {
                                            setPendingParentFamilyId(value);
                                            if (option?.workTypeId) {
                                                setPendingParentWorkTypeId(option.workTypeId);
                                                setShowImportWorkTypeModal(true);
                                            } else {
                                                setPendingParentWorkTypeId(null);
                                                setShowImportParametersModal(true);
                                            }
                                        } else {
                                            setPendingParentFamilyId(null);
                                            setPendingParentWorkTypeId(null);
                                        }
                                    }}
                                />
                                <InputError error={errors.parentId} />
                            </Col>
                        )}
                        <Col xs={6} className="mb-3">
                            <Label space>{t('families.family.work_type')}</Label>
                            <SelectInputController
                                control={form.control}
                                name='workTypeId'
                                options={workTypes || []}
                                disabled={isDetails}
                            />
                        </Col>
                        {listType === 'elements' && (
                            <Col xs={6} md={3} className="mb-3">
                                <Label space>
                                    {t('families.family.coefficient')}
                                    <OverlayTrigger
                                        placement='top'
                                        overlay={<Tooltip id='family-coefficient-info'>{t('families.family.coefficient_info')}</Tooltip>}
                                    >
                                        <span><InfoIcon /></span>
                                    </OverlayTrigger>
                                </Label>
                                <div className={styles.percentInputGroup}>
                                    <TextInputController
                                        name='coefficient'
                                        control={form.control}
                                        placeholder={t('families.family.coefficient_placeholder')}
                                        disabled={isDetails}
                                        type='number'
                                        hasError={Boolean(errors.coefficient)}
                                    />
                                    <span className={styles.percentSuffix}>%</span>
                                </div>
                                <InputError error={errors.coefficient} />
                            </Col>
                        )}
                        {listType === 'elements' && type !== 'create' && (
                            <Col xs={6} md={3} className="mb-3">
                                <Label space>{t('families.family.family_average')}</Label>
                                <TextInput
                                    value={family?.averageValue != null ? Utils.formatCurrency(family.averageValue) : ''}
                                    onChange={() => {}}
                                    disabled
                                />
                            </Col>
                        )}
                    </Row>
                    {/* Family Type - Commented out, always defaults to Element */}
                    {/* <Row className="mb-3">
                        <Col xs={12}>
                            <Label space>{t('families.family.type')}
                                {!isDetails ? '*' : ''}
                                {isSubFamilies && <InfoIcon onClick={() => setShowTypesInformationModal(true)} />}
                            </Label>

                            <CheckInputController
                                name='isComponent'
                                control={form.control}
                                placeholder={t('families.family.component')}
                                disabled={isDetails || isSubFamilies}
                                onChange={() => {
                                    void form.trigger('isElement');
                                    onChangeFamilyType();
                                }}
                            >
                                {t('families.family.component')}
                            </CheckInputController>
                            <CheckInputController
                                name='isElement'
                                control={form.control}
                                placeholder={t('families.family.element')}
                                disabled={isDetails || isSubFamilies}
                                rules={{
                                    validate: () => {
                                        return !!(form.watch('isComponent') || form.watch('isElement')) || 'error';
                                    }
                                }}
                                onChange={() => {
                                    onChangeFamilyType();
                                }}
                            >
                                {t('families.family.element')}
                            </CheckInputController>
                            <InputError error={errors.isComponent || errors.isElement} />
                        </Col>
                    </Row> */}
                    <Row className="mb-3">
                        <Col xs={12}>
                            <Label space>{t('families.family.description')}</Label>
                            <RichInputController
                                name='description'
                                control={form.control}
                                placeholder={t('families.family.description')}
                                disabled={isDetails}
                            />
                        </Col>
                    </Row>
                    <Row className="mb-3">
                        <Col>
                            <Label space>{t('families.family.images')}</Label>
                            <FilesUploader
                                files={medias}
                                type='images'
                                onChange={setMedias}
                                containerName={(f) => f.key ?? ''}
                                canSetMain
                                disabled={isDetails}
                            />
                        </Col>
                    </Row>
                    {listType !== 'components' && (!isDetails || priceExpression || averageExpression) && (
                        <Row className="mb-3">
                            {(!isDetails || averageExpression) && (
                                <Col xs={6}>
                                    <Label space>{t('families.family.average_formula')}</Label>
                                    <TextInput
                                        value={ExpressionSystem.formatExpressionForDisplay(
                                            averageExpression ?? '',
                                            loadFamilyAverageFormulaParameters()
                                        )}
                                        onChange={() => {}}
                                        placeholder={t('families.family.average_formula_placeholder')}
                                        readOnly={true}
                                        style={{ cursor: 'default' }}
                                        expressionButton={{
                                            value: averageExpression ?? '',
                                            title: t('families.family.average_formula'),
                                            loadParameters: loadFamilyAverageFormulaParameters,
                                            onChange: (expr: string | null) => setAverageExpression(expr),
                                            disabled: isDetails,
                                            hideResult: true,
                                        }}
                                    />
                                </Col>
                            )}
                            {(!isDetails || priceExpression) && (
                                <Col xs={6}>
                                    <Label space>{t('families.family.price_formula')}</Label>
                                    <TextInput
                                        value={ExpressionSystem.formatExpressionForDisplay(
                                            priceExpression ?? '',
                                            loadFamilyPriceFormulaParameters()
                                        )}
                                        onChange={() => {}}
                                        placeholder={t('families.family.price_formula_placeholder')}
                                        readOnly={true}
                                        style={{ cursor: 'default' }}
                                        expressionButton={{
                                            value: priceExpression ?? '',
                                            title: t('families.family.price_formula'),
                                            loadParameters: loadFamilyPriceFormulaParameters,
                                            onChange: (expr: string | null) => setPriceExpression(expr),
                                            disabled: isDetails,
                                            hideResult: true,
                                        }}
                                    />
                                </Col>
                            )}
                        </Row>
                    )}

                    {sections?.length
                        ? <FamilyParameterDataProvider.Provider value={{ family, familiesInputOptions, units, selectedSectionKey, sections, weightUnitId, catalogParameters, onCatalogParameterCreated: (param) => setCatalogParameters(prev => [...prev, param]) }}>
                            <SortableTree
                                items={sections}
                                onItemsChanged={(newItems, reason: ItemChangedReason<ParameterSectionBaseModel>) => {
                                    let items = newItems as ParameterSectionBaseModel[];
                                    if (reason.type === 'dropped') {
                                        items = normalizeSubsectionVisibility(items);
                                    }
                                    setSections(items);
                                }}
                                onItemChange={onItemChange}
                                onItemCreate={onItemCreate}
                                onMoveItem={onMoveItem}
                                onSelectItem={setSelectedSectionKey}
                                TreeItemComponent={ParameterLine}
                                canHaveChildren={(item) => (item.type === ParameterSectionType.SECTION)}
                                canRootHaveChildren={true}
                                dropAnimation={null}
                                indentationWidth={20}
                                disableSorting={isDetails}
                            />
                        </FamilyParameterDataProvider.Provider>
                        : isDetails
                            ? null
                            : <div className="text-center">
                                <Button variant={'third'} className={`${styles.addButton}`} onClick={onClickAddNewSection}>
                                    <IoAddSharp fontSize={18} />
                                    <span className='v-align-middle'>
                                        {t('common.parameters_sections.click_add_section')}
                                    </span>
                                </Button>
                            </div>
                    }
                </Form>
            </PageContainer>

            <ConfirmDeleteModal
                itemName={form.getValues('name')}
                isOpen={showConfirmDeleteModal}
                onClose={onDelete}
            />

            <YesNoModal
                title={t('common.question')}
                message={listType === 'components' ? t('families.delete_components_relations') : t('families.delete_elements_relations')}
                onClose={onDeleteRelations}
                isOpen={showConfirmDeleteRelationsModal}
            />

            <InformationModal
                message={t('families.family.types_information')}
                isOpen={showTypesInformationModal}
                onClose={() => setShowTypesInformationModal(false)}
            />

            <YesNoModal
                title={t('families.family.import_parameters_title')}
                message={sections.length > 0
                    ? t('families.family.import_parameters_replace_confirm')
                    : t('families.family.import_parameters_confirm')}
                onClose={onConfirmImportParameters}
                isOpen={showImportParametersModal}
            />

            <YesNoModal
                title={t('families.family.import_work_type_title')}
                message={t('families.family.import_work_type_confirm')}
                onClose={onConfirmImportWorkType}
                isOpen={showImportWorkTypeModal}
            />

            <YesNoModal
                title={t('families.family.apply_work_type_to_subfamilies_title')}
                message={t('families.family.apply_work_type_to_subfamilies_confirm')}
                onClose={onConfirmCascadeWorkType}
                isOpen={showCascadeWorkTypeModal}
            />
        </ScreenTitle>
    );
}

export default FamilyScreen;
